import React, { useEffect, useState } from "react";
import { Table, Input, Button, Form, Space, Tooltip, Select } from "antd";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { updateTourSealNumbers } from "../../helpers/updateSealNumber";
import { toast } from "react-toastify";
import { ArrowDownOutlined } from "@ant-design/icons";

const { Option } = Select;

const SealedValueTable = ({
  tourDetails,
  isRider,
  handleError,
  authToken,
  clientId,
  setEnableTabel,
}) => {
  const tourSealNumber = useSelector(
    (state) => state?.tourSealNumber?.allSealNumber
  );

  const [numberOfGates, setNumberOfGates] = useState(tourDetails.numberOfGates);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [locationSealNumber, setLocationSealNumber] = useState({});
  const [uniqueLocationIds, setUniqueLocationIds] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (isRider && tourDetails?.locationSealNumber) {
      setUniqueLocationIds(Object.keys(tourDetails.locationSealNumber));
    } else {
      if (
        tourDetails?.tour?.tasks &&
        Array.isArray(tourDetails.tour.tasks) &&
        tourDetails?.tour?.plannedSequence?.visits
      ) {
        const uniqueLocationIds = Array.from(
          new Set(
            tourDetails?.tour?.plannedSequence?.visits?.flatMap((element) => {
              if (
                element?.visitId?.visitSource === "TASK" &&
                element?.visitId?.id === "customer"
              ) {
                return tourDetails.tour.tasks.flatMap((task) => {
                  if (
                    element.visitId.sourceId === task?.taskId &&
                    task.taskGraph?.visits &&
                    Array.isArray(task.taskGraph.visits)
                  ) {
                    return task.taskGraph.visits
                      .filter(
                        (visit) =>
                          visit.id === "customer" || visit.id === "homebase"
                      )
                      .map((visit) => visit.locationIds?.[0]?.locationId)
                      .filter((locationId) => locationId !== undefined);
                  }
                  return [];
                });
              }
              return [];
            })
          )
        );

        setUniqueLocationIds(uniqueLocationIds);
      }
    }
  }, [tourDetails, isRider]);

  useEffect(() => {
    setNumberOfGates(tourDetails.numberOfGates);
    if (!isEmpty(tourSealNumber)) {
      if (isRider) {
        setNumberOfGates(tourSealNumber?.numberOfGates);
      }
      setLocationSealNumber(tourSealNumber?.locationSealNumber);
    }
  }, [tourSealNumber, isRider, tourDetails.numberOfGates]);

  useEffect(() => {
    const dataSource = uniqueLocationIds.map((locationId, index) => {
      if (isEmpty(locationSealNumber)) {
        return {
          key: `${index}`,
          location: locationId,
          ...Object.fromEntries(
            Array.from({ length: numberOfGates }, (_, gateIndex) => [
              `gate_${gateIndex + 1}`,
              "",
            ])
          ),
        };
      }
      return {
        key: `${index}`,
        location: locationId,
        ...Object.fromEntries(
          Array.from({ length: numberOfGates }, (_, gateIndex) => [
            `gate_${gateIndex + 1}`,
            locationSealNumber?.[locationId]?.[gateIndex] ?? "",
          ])
        ),
      };
    });
    setData(dataSource);
  }, [setData, numberOfGates, locationSealNumber, uniqueLocationIds]);

  const handleChange = (record, colIndex, value) => {
    const newData = [...data];
    const index = newData.findIndex((item) => record.key === item.key);

    if (index > -1) {
      const item = newData[index];
      item[`gate_${colIndex}`] = value;
      newData.splice(index, 1, { ...item });
      setData(newData);
    }
  };

  const handleFinish = async (values) => {
    const transformedData = await data.reduce((result, item) => {
      const { location, key, ...gateValues } = item;

      if (!result[location]) {
        result[location] = [];
      }

      const gateEntries = Object.entries(gateValues)
        .filter(([key]) => key.startsWith("gate_"))
        .map(([, value]) => value);

      result[location].push(...gateEntries);

      return result;
    }, {});
    const payload = {
      riderId: tourDetails?.riderId,
      tourId: tourDetails?.tourId,
      date: tourDetails?.date,
      numberOfGates: numberOfGates,
      loadSealNumber: transformedData,
    };

    const authParams = {
      clientId,
      authToken,
      data: payload,
    };

    const updateTourSealNumber = await updateTourSealNumbers(authParams);

    if (!updateTourSealNumber?.status) {
      toast.error(
        `Seal number update failure! ${
          updateTourSealNumber?.data?.message || ""
        }`,
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        }
      );
    } else {
      toast.success(`Seal number has been updated!`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      setTimeout(() => {
        setEnableTabel(false);
      }, 2000);
    }
  };

  const handleCopyDown = (colIndex) => {
    const newData = [...data];
    const lastIndex = newData.length;

    for (let i = 1; i < lastIndex; i++) {
      newData[i][`gate_${colIndex}`] = newData[0][`gate_${colIndex}`];
    }

    setData(newData);
  };

  const columns = [
    {
      title: (
        <Select
          placeholder="Select location"
          style={{ width: "100%" }}
          onChange={(value) => setSelectedLocation(value)}
          allowClear
        >
          {uniqueLocationIds.map((locationId) => (
            <Option key={locationId} value={locationId}>
              {locationId}
            </Option>
          ))}
        </Select>
      ),
      dataIndex: "location",
      width: 100,
      render: (text) => <span>{text}</span>,
    },
    ...Array.from({ length: numberOfGates }, (_, index) => ({
      title: (
        <div>
          {`Gate ${index + 1}`}
          {!isRider && (
            <Button type="link" size="small">
              <Tooltip title="Copy first to last" placement="bottom">
                <ArrowDownOutlined onClick={() => handleCopyDown(index + 1)} />
              </Tooltip>
            </Button>
          )}
        </div>
      ),
      dataIndex: `gate_${index + 1}`,
      width: 100,
      render: (_, record) => {
        return isRider ? (
          <span> {record[`gate_${index + 1}`]} </span>
        ) : (
          <Input
            value={record[`gate_${index + 1}`]}
            onChange={(e) => handleChange(record, index + 1, e.target.value)}
            required={!isRider}
          />
        );
      },
    })),
  ];

  const filteredData = selectedLocation
    ? (() => {
        const index = data.findIndex(
          (item) => item.location === selectedLocation
        );
        return data.slice(index, index + 2);
      })()
    : data;

  return (
    <>
      <Form form={form} onFinish={handleFinish}>
        <Table
          bordered
          dataSource={filteredData}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
        />
        {!isRider && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Space>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </Space>
          </div>
        )}
      </Form>
    </>
  );
};

export default SealedValueTable;
